import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Calendar, DollarSign, Building, FileText, Shield, Send, Award, XCircle, Loader2, MessageSquare, History, Plus, Users } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { rfqAPI } from '../lib/api';
import type { RFQ, RFQQuery, SupplierResponseListItem, RFQActivityLog } from '../lib/api';
import { useFormatters } from '../contexts/PreferencesContext';
import { useAuth } from '../contexts/AuthContext';
import { useIsSupplier } from '../hooks/usePortalMode';
import ResponseComparison from '../components/rfq/ResponseComparison';
import QueryThread from '../components/rfq/QueryThread';
import SupplierResponseForm from '../components/rfq/SupplierResponseForm';
import type { SupplierResponse } from '../lib/api';


export default function RFQDetail() {
  const { formatCurrency: formatCurrencyFromHook } = useFormatters()
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [rfq, setRfq] = useState<RFQ | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // New state for tabs
  const [queries, setQueries] = useState<RFQQuery[]>([]);
  const [responses, setResponses] = useState<SupplierResponseListItem[]>([]);
  const [activityLogs, setActivityLogs] = useState<RFQActivityLog[]>([]);
  const [showCreateQueryDialog, setShowCreateQueryDialog] = useState(false);
  const [newQuerySubject, setNewQuerySubject] = useState('');
  const [newQueryMessage, setNewQueryMessage] = useState('');
  const [creatingQuery, setCreatingQuery] = useState(false);

  // Supplier response state
  const [myResponse, setMyResponse] = useState<SupplierResponse | null>(null);

  // Determine if user is a supplier viewing this RFQ
  const isSupplier = useIsSupplier();
  const userCompanyId = user?.company;

  useEffect(() => {
    fetchRFQDetail();
    fetchQueries();
    fetchResponses();
    fetchActivityLogs();
  }, [id, isSupplier]);

  // Fetch supplier's own response when responses are loaded
  useEffect(() => {
    if (isSupplier && userCompanyId && responses.length > 0) {
      const supplierResponse = responses.find(r => r.supplier === userCompanyId);
      if (supplierResponse) {
        fetchMyResponse(supplierResponse.id);
      }
    }
  }, [isSupplier, userCompanyId, responses]);

  const fetchMyResponse = async (responseId: number) => {
    try {
      const response = await rfqAPI.getResponse(responseId);
      setMyResponse(response.data);
    } catch (error) {
      console.error('Error fetching my response:', error);
    }
  };

  const fetchRFQDetail = async () => {
    try {
      setLoading(true);
      const response = await rfqAPI.getRFQ(Number(id));
      setRfq(response.data);
    } catch (error) {
      console.error('Error fetching RFQ details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQueries = async () => {
    try {
      const response = await rfqAPI.listQueries(Number(id));
      setQueries(response.data.results || response.data || []);
    } catch (error) {
      console.error('Error fetching queries:', error);
    }
  };

  const fetchResponses = async () => {
    try {
      const response = await rfqAPI.listResponses(Number(id));
      setResponses(response.data.results || response.data || []);
    } catch (error) {
      console.error('Error fetching responses:', error);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      const response = await rfqAPI.getActivityLog(Number(id));
      setActivityLogs(response.data.results || response.data || []);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    }
  };

  const handleCreateQuery = async () => {
    if (!newQuerySubject.trim() || !newQueryMessage.trim()) {
      toast.error('Please enter a subject and message');
      return;
    }
    try {
      setCreatingQuery(true);
      await rfqAPI.createQuery({
        rfq: Number(id),
        subject: newQuerySubject.trim(),
        message: newQueryMessage.trim(),
      });
      toast.success('Query created successfully');
      setShowCreateQueryDialog(false);
      setNewQuerySubject('');
      setNewQueryMessage('');
      fetchQueries();
    } catch (error) {
      console.error('Error creating query:', error);
      toast.error('Failed to create query');
    } finally {
      setCreatingQuery(false);
    }
  };

  const handleSendToSuppliers = async () => {
    if (!rfq) return;
    try {
      setActionLoading(true);
      await rfqAPI.sendToSuppliers(rfq.id, { supplier_ids: [] });
      toast.success('RFQ sent to suppliers successfully');
      setShowSendDialog(false);
      fetchRFQDetail();
    } catch (error) {
      console.error('Error sending RFQ:', error);
      toast.error('Failed to send RFQ to suppliers');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!rfq) return;
    try {
      setActionLoading(true);
      await rfqAPI.cancelRFQ(rfq.id, { reason: cancelReason });
      toast.success('RFQ cancelled successfully');
      setShowCancelDialog(false);
      setCancelReason('');
      fetchRFQDetail();
    } catch (error) {
      console.error('Error cancelling RFQ:', error);
      toast.error('Failed to cancel RFQ');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      responded: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-300 text-gray-700',
    };
    return colors[status] || 'bg-gray-100';
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'bg-gray-100 text-gray-600',
      medium: 'bg-blue-100 text-blue-600',
      high: 'bg-orange-100 text-orange-600',
      urgent: 'bg-red-100 text-red-600',
    };
    return colors[priority] || 'bg-gray-100';
  };

  const formatCurrency = (amount: number | string | null, currency: string) => {
    if (amount === null) return '-';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return formatCurrencyFromHook(numAmount, currency);
  };

  if (loading) {
    return <div className="p-6 text-center">Loading...</div>;
  }

  if (!rfq) {
    return <div className="p-6 text-center">RFQ not found</div>;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/rfq')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-3xl font-bold">{rfq.rfq_number}</h1>
            <Badge className={getStatusColor(rfq.status)}>{rfq.status.toUpperCase()}</Badge>
            <Badge className={getPriorityColor(rfq.priority)}>{rfq.priority.toUpperCase()}</Badge>
          </div>
          <p className="text-xl text-muted-foreground">{rfq.title}</p>
        </div>
        {/* Action Buttons */}
        <div className="flex gap-2">
          {rfq.status === 'draft' && (
            <Button onClick={() => setShowSendDialog(true)} className="gap-2">
              <Send className="h-4 w-4" />
              Send to Suppliers
            </Button>
          )}
          {(rfq.status === 'sent' || rfq.status === 'submitted') && (
            <Button variant="default" className="gap-2" disabled>
              <Award className="h-4 w-4" />
              Award RFQ
            </Button>
          )}
          {rfq.status !== 'cancelled' && rfq.status !== 'awarded' && (
            <Button variant="outline" onClick={() => setShowCancelDialog(true)} className="gap-2 text-destructive hover:text-destructive">
              <XCircle className="h-4 w-4" />
              Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Total Value
            </CardDescription>
            <CardTitle className="text-2xl">{formatCurrency(rfq.total_estimated_value, rfq.currency)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Response Deadline
            </CardDescription>
            <CardTitle className="text-lg">{new Date(rfq.response_deadline).toLocaleDateString()}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Building className="h-4 w-4" />
              {rfq.awarded_supplier_name ? 'Awarded Supplier' : 'Customer'}
            </CardDescription>
            <CardTitle className="text-lg">{rfq.awarded_supplier_name || rfq.customer_name}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Category
            </CardDescription>
            <CardTitle className="text-lg capitalize">{rfq.category}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="line-items">Line Items ({rfq.line_items?.length || 0})</TabsTrigger>
          <TabsTrigger value="responses" className="flex items-center gap-1">
            <Users className="h-3 w-3" />
            Responses ({responses.length})
          </TabsTrigger>
          <TabsTrigger value="queries" className="flex items-center gap-1">
            <MessageSquare className="h-3 w-3" />
            Queries ({queries.length})
          </TabsTrigger>
          <TabsTrigger value="certificates">Certificates ({rfq.required_certificates?.length || 0})</TabsTrigger>
          <TabsTrigger value="documents">Documents ({rfq.documents?.length || 0})</TabsTrigger>
          <TabsTrigger value="activity" className="flex items-center gap-1">
            <History className="h-3 w-3" />
            Activity
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap text-sm">{rfq.description}</p>
            </CardContent>
          </Card>

          {rfq.special_requirements && (
            <Card>
              <CardHeader>
                <CardTitle>Special Requirements</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap text-sm">{rfq.special_requirements}</p>
              </CardContent>
            </Card>
          )}

          {rfq.terms_and_conditions && (
            <Card>
              <CardHeader>
                <CardTitle>Terms & Conditions</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap text-sm">{rfq.terms_and_conditions}</p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Additional Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Issue Date:</span>
                <span className="font-medium">{rfq.issue_date ? new Date(rfq.issue_date).toLocaleDateString() : 'Not issued'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Validity Period:</span>
                <span className="font-medium">{rfq.validity_period_days} days</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Currency:</span>
                <span className="font-medium">{rfq.currency}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Created:</span>
                <span className="font-medium">{new Date(rfq.created_at).toLocaleDateString()}</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Line Items Tab */}
        <TabsContent value="line-items" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium">#</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Part Number</th>
                      <th className="px-4 py-3 text-right text-sm font-medium">Quantity</th>
                      <th className="px-4 py-3 text-right text-sm font-medium">Unit Price</th>
                      <th className="px-4 py-3 text-right text-sm font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {rfq.line_items?.map((item) => (
                      <tr key={item.id} className="hover:bg-muted/50">
                        <td className="px-4 py-3 text-sm">{item.line_number}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{item.item_description}</div>
                          {item.technical_specifications && (
                            <div className="text-xs text-muted-foreground mt-1">{item.technical_specifications}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">{item.part_number}</td>
                        <td className="px-4 py-3 text-sm text-right">
                          {item.quantity.toLocaleString()} {item.unit_of_measure}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          {item.target_unit_price ? formatCurrency(item.target_unit_price, rfq.currency) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium">
                          {formatCurrency(item.estimated_total_price, rfq.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-muted font-medium">
                    <tr>
                      <td colSpan={5} className="px-4 py-3 text-right">Total Estimated Value:</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(rfq.total_estimated_value, rfq.currency)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Certificates Tab */}
        <TabsContent value="certificates" className="space-y-4">
          {rfq.required_certificates?.length > 0 ? (
            <div className="grid gap-4">
              {rfq.required_certificates.map((cert) => (
                <Card key={cert.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-primary" />
                        <CardTitle className="text-lg">{cert.certificate_name}</CardTitle>
                      </div>
                      <Badge variant={cert.is_mandatory ? 'default' : 'secondary'}>
                        {cert.is_mandatory ? 'MANDATORY' : 'OPTIONAL'}
                      </Badge>
                    </div>
                    <CardDescription className="capitalize">{cert.certificate_type.replace('_', ' ')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm mb-2">{cert.description}</p>
                    <p className="text-xs text-muted-foreground">
                      Validity required: {cert.expiry_tolerance_days} days beyond delivery
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-12 text-muted-foreground">
                No certificates required for this RFQ
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          {rfq.documents?.length > 0 ? (
            <div className="grid gap-4">
              {rfq.documents.map((doc) => (
                <Card key={doc.id}>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      <CardTitle className="text-lg">{doc.title}</CardTitle>
                    </div>
                    <CardDescription className="capitalize">{doc.document_type.replace('_', ' ')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm mb-2">{doc.description}</p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>File: {doc.file_name}</span>
                      <span>Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-12 text-muted-foreground">
                No documents attached to this RFQ
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Responses Tab */}
        <TabsContent value="responses" className="space-y-4">
          {/* Supplier View: Show their own response form */}
          {isSupplier && myResponse ? (
            <SupplierResponseForm
              rfq={rfq}
              response={myResponse}
              onSuccess={() => {
                fetchMyResponse(myResponse.id);
                fetchResponses();
              }}
              onClose={() => navigate('/rfq')}
            />
          ) : isSupplier && !myResponse && responses.length > 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Loading your response...</p>
              </CardContent>
            </Card>
          ) : isSupplier ? (
            <Card>
              <CardContent className="text-center py-12">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">You are not invited to respond to this RFQ.</p>
              </CardContent>
            </Card>
          ) : responses.length > 0 ? (
            /* Customer View: Show response comparison */
            <ResponseComparison rfq={rfq} onAward={fetchRFQDetail} />
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No supplier responses yet.</p>
                {rfq.status === 'draft' && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Send the RFQ to suppliers to start receiving responses.
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Queries Tab */}
        <TabsContent value="queries" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Query Threads</h3>
            <Button onClick={() => setShowCreateQueryDialog(true)} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              New Query
            </Button>
          </div>
          {queries.length > 0 ? (
            <div className="space-y-4">
              {queries.map((query) => (
                <QueryThread
                  key={query.id}
                  query={query}
                  currentUserId={user?.id || 0}
                  onRefresh={fetchQueries}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No queries yet.</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Create a query to communicate with suppliers about this RFQ.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Activity History
              </CardTitle>
              <CardDescription>Timeline of changes and actions for this RFQ</CardDescription>
            </CardHeader>
            <CardContent>
              {activityLogs.length > 0 ? (
                <div className="space-y-4">
                  {activityLogs.map((log, index) => (
                    <div key={log.id} className="flex gap-4">
                      <div className="relative">
                        <div className="h-3 w-3 rounded-full bg-primary mt-1.5" />
                        {index < activityLogs.length - 1 && (
                          <div className="absolute left-1.5 top-4 bottom-0 w-px bg-border h-full" />
                        )}
                      </div>
                      <div className="flex-1 pb-4">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium">{log.action}</span>
                          <span className="text-muted-foreground">by {log.performed_by_name}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{log.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(log.performed_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No activity recorded yet.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Send to Suppliers Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send RFQ to Suppliers</DialogTitle>
            <DialogDescription>
              This will send the RFQ to all associated suppliers and change the status to &quot;Sent&quot;.
              Suppliers will be notified and can begin submitting their responses.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={handleSendToSuppliers} disabled={actionLoading} className="gap-2">
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              Send to Suppliers
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel RFQ Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel RFQ</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this RFQ? This action cannot be undone.
              All suppliers will be notified of the cancellation.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">Reason for cancellation (optional)</label>
            <textarea
              className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Enter reason for cancellation..."
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)} disabled={actionLoading}>
              Keep RFQ
            </Button>
            <Button variant="destructive" onClick={handleCancel} disabled={actionLoading} className="gap-2">
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              Cancel RFQ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Query Dialog */}
      <Dialog open={showCreateQueryDialog} onOpenChange={setShowCreateQueryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Query</DialogTitle>
            <DialogDescription>
              Start a new query thread to communicate with suppliers about this RFQ.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Subject</label>
              <Input
                value={newQuerySubject}
                onChange={(e) => setNewQuerySubject(e.target.value)}
                placeholder="Enter query subject..."
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Message</label>
              <Textarea
                value={newQueryMessage}
                onChange={(e) => setNewQueryMessage(e.target.value)}
                placeholder="Enter your message..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateQueryDialog(false);
                setNewQuerySubject('');
                setNewQueryMessage('');
              }}
              disabled={creatingQuery}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateQuery} disabled={creatingQuery} className="gap-2">
              {creatingQuery && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Query
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
